import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { journeyActions } from "../../store/journeySlice";
import {
  getNischitProductConfig,
  resolveNischitProductCode,
} from "./productConfig";
import { calculateAge } from "../../common/utils/formatters";
import { TEXT_CONTENT } from "../../constants";
import { useJourney } from "../../common/hooks/useJourney";

const MAX_PREMIUM_AMOUNT = 99999999999999;
const PREMIUM_FREQUENCY_DIVISOR = {
  yearly: 1.000,
  half_yearly: 0.505,
  quarterly: 0.255,
  monthly: 0.087,
};
const isSecondPlanOption = (planOptionRaw) => {
  const raw = String(planOptionRaw ?? "").trim().toLowerCase();
  if (!raw) return false;
  return (
    raw === "option_2" ||
    raw === "option2" ||
    raw === "2" ||
    raw.includes("option_2") ||
    raw.includes("option 2")
  );
};
const resolveMinimumPremiumByRules = (productCode, pptRaw, planOptionRaw) => {
  const product = String(productCode || "22070");
  const ppt = String(pptRaw || "");

  // Nishchit Pension Pro (Single Pay): Any of 6 options => 2,00,000
  if (product === "22074") return 200000;

  // Nishchit Pension (22070)
  // PPT 2 / 3 => Any of 5 plan options => 1,50,000
  if (ppt === "2" || ppt === "3") return 150000;

  // PPT 5/6/7/8/10:
  // Option 2 => 75,000
  // Option 1/3/4/5 => 1,00,000
  if (["5", "6", "7", "8", "10"].includes(ppt)) {
    if (isSecondPlanOption(planOptionRaw)) return 75000;
    return 100000;
  }

  // Safe fallback
  return 100000;
};

const customizePlanSchema = {
  availableLanguageIds: ["en_US"],
  defaultLanguageId: "en_US",
  fields: [
    {
      labelAtStructureLevel: true,
      repeatable: false,
      predefinedValue: {},
      name: "FieldsetCustomizePlan",
      fieldReference: "customizePlan",
      tip: {},
      label: { en_US: "Customize your Plan" },
      type: "fieldset",
      rows: JSON.stringify([
        { columns: [{ size: 12, fields: ["premiumAmount"] }] },
        { columns: [{ size: 12, fields: ["premiumPayingTerm"] }] },
        { columns: [{ size: 12, fields: ["premiumFrequency"] }] },
        { columns: [{ size: 12, fields: ["withdrawalAfter"] }] },
        { columns: [{ size: 12, fields: ["annuityFrequency"] }] },
      ]),
      collapsible: false,
      showLabel: true,
      nestedFields: [
        {
          labelAtStructureLevel: true,
          type: "numeric",
          dataType: "integer",
          name: "premiumAmount",
          fieldReference: "premiumAmount",
          label: { en_US: "Premium Amount" },
          placeholder: { en_US: "₹ 1,00,000" },
          predefinedValue: {},
          required: true,
          repeatable: false,
          readOnly: false,
        },
        {
          labelAtStructureLevel: true,
          type: "select",
          dataType: "string",
          name: "premiumPayingTerm",
          fieldReference: "premiumPayingTerm",
          label: { en_US: "Premium Paying Term" },
          predefinedValue: {},
          required: true,
          repeatable: false,
          options: [
            { reference: "2", label: { en_US: "2 years" }, value: "2" },
            { reference: "3", label: { en_US: "3 years" }, value: "3" },
            { reference: "5", label: { en_US: "5 years" }, value: "5" },
            { reference: "6", label: { en_US: "6 years" }, value: "6" },
            { reference: "7", label: { en_US: "7 years" }, value: "7" },
            { reference: "8", label: { en_US: "8 years" }, value: "8" },
            { reference: "10", label: { en_US: "10 years" }, value: "10" },
          ],
        },
        {
          labelAtStructureLevel: true,
          type: "select",
          dataType: "string",
          name: "premiumFrequency",
          fieldReference: "premiumFrequency",
          label: { en_US: "Premium Paying Frequency" },
          predefinedValue: {},
          required: true,
          repeatable: false,
          options: [
            {
              reference: "monthly",
              label: { en_US: "Monthly" },
              value: "monthly",
            },
            {
              reference: "quarterly",
              label: { en_US: "Quarterly" },
              value: "quarterly",
            },
            {
              reference: "half_yearly",
              label: { en_US: "Half-Yearly" },
              value: "half_yearly",
            },
            {
              reference: "yearly",
              label: { en_US: "Yearly" },
              value: "yearly",
            },
          ],
        },
        {
          labelAtStructureLevel: true,
          type: "select",
          dataType: "string",
          name: "withdrawalAfter",
          fieldReference: "withdrawalAfter",
          label: { en_US: "Withdrawal After" },
          predefinedValue: {},
          required: true,
          repeatable: false,
          options: [
            { reference: "5", label: { en_US: "5 years" }, value: "5" },
            { reference: "6", label: { en_US: "6 years" }, value: "6" },
            { reference: "7", label: { en_US: "7 years" }, value: "7" },
            { reference: "8", label: { en_US: "8 years" }, value: "8" },
            { reference: "9", label: { en_US: "9 years" }, value: "9" },
            { reference: "10", label: { en_US: "10 years" }, value: "10" },
            { reference: "15", label: { en_US: "15 years" }, value: "15" },
          ],
        },
        {
          labelAtStructureLevel: true,
          type: "select",
          dataType: "string",
          name: "annuityFrequency",
          fieldReference: "annuityFrequency",
          label: { en_US: "Annuity Payment Frequency" },
          predefinedValue: {},
          required: true,
          repeatable: false,
          options: [
            {
              reference: "monthly",
              label: { en_US: "Monthly" },
              value: "monthly",
            },
            {
              reference: "quarterly",
              label: { en_US: "Quarterly" },
              value: "quarterly",
            },
            {
              reference: "half_yearly",
              label: { en_US: "Half-Yearly" },
              value: "half_yearly",
            },
            {
              reference: "yearly",
              label: { en_US: "Yearly" },
              value: "yearly",
            },
          ],
        },
      ],
    },
  ],
};

const CustomizePlan = ({ hideCalculateButton = false, onValidationChange, setIsFormDirtyAfterCalculate}) => {
  const dispatch = useDispatch();

  useJourney();

  // Get fieldset
  const fieldset = customizePlanSchema.fields.find(
    (f) => f.fieldReference === "customizePlan",
  );

  const formFields = fieldset?.nestedFields || [];

  const savedData = useSelector((state) => state.journey.customizePlanData);
  const leadFormData = useSelector((state) => state.journey.leadFormData || {});
  const currentProductCode = resolveNischitProductCode(leadFormData);
  const currentProductConfig = getNischitProductConfig(currentProductCode);
  const isSinglePayProduct = String(currentProductCode) === "22074";
  const [isCalculateClicked, setIsCalculateClicked] = useState(false);
  const primaryAnnuitantAge = useMemo(() => {
    const resolvedDob =
      leadFormData?.annuitantDateOfBirth ||
      leadFormData?.primaryAnnuitantDob ||
      leadFormData?.dateOfBirth ||
      leadFormData?.lifeAssuredDob ||
      leadFormData?.proposerDob ||
      "";
    return calculateAge(resolvedDob);
  }, [
    leadFormData?.annuitantDateOfBirth,
    leadFormData?.primaryAnnuitantDob,
    leadFormData?.dateOfBirth,
    leadFormData?.lifeAssuredDob,
    leadFormData?.proposerDob,
  ]);
   const customizePlanData = useSelector(
      (s) => s.journey.customizePlanData || {},
    );

  const secondaryAnnuitantAge = useMemo(() => {
  const resolvedDob =
    customizePlanData?.secondaryAnnuitantDob ||
    "";

  return calculateAge(resolvedDob);
}, [
 customizePlanData?.secondaryAnnuitantDob
]);
const olderAnnuitantAge = useMemo(() => {
  const ages = [
    primaryAnnuitantAge,
    secondaryAnnuitantAge,
  ].filter((age) => Number.isFinite(age));

  if (!ages.length) return null;

  return Math.max(...ages);
}, [primaryAnnuitantAge, secondaryAnnuitantAge]);
  const activePlanOptionId = useMemo(
    () => String(savedData?.selectedNishchitPlanId || savedData?.planOption || ""),
    [savedData?.selectedNishchitPlanId, savedData?.planOption],
  );

  // Initialize form values from schema
  const [formValues, setFormValues] = useState(() => {
    const initial = {};

    formFields.forEach((field) => {
      initial[field.name] = savedData?.[field.name] ?? "";
    });

    return initial;
  });

  useEffect(() => {
  if (!savedData || Object.keys(savedData).length === 0) return;

  setFormValues((prev) => ({
    ...prev,
    premiumAmount: savedData?.premiumAmount ?? prev?.premiumAmount ?? "",
    premiumPayingTerm:
      savedData?.premiumPayingTerm ?? prev?.premiumPayingTerm ?? "",
    withdrawalAfter:
      savedData?.withdrawalAfter ?? prev?.withdrawalAfter ?? "",
    annuityFrequency:
      savedData?.annuityFrequency ?? prev?.annuityFrequency ?? "",
  }));
}, [savedData]);

  const [premiumAmountError, setPremiumAmountError] = useState("");
  const [hasEditedPremiumAmount, setHasEditedPremiumAmount] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const hasErrors =
      Object.keys(fieldErrors || {}).length > 0 ||
      (hasEditedPremiumAmount && Boolean(premiumAmountError));

    onValidationChange?.(hasErrors);
  }, [
    fieldErrors,
    premiumAmountError,
    hasEditedPremiumAmount,
    onValidationChange,
  ]);

  // useEffect(() => {
  //   dispatch(journeyActions.setCustomizePlanData(formValues));
  // }, []);

  const minAllowedPremium = useMemo(
    () =>
      resolveMinimumPremiumByRules(
        currentProductCode,
        formValues?.premiumPayingTerm,
        activePlanOptionId,
      ),
    [
      currentProductCode,
      formValues?.premiumPayingTerm,
      activePlanOptionId,
    ],
  );
  const minAllowedPremiumByFrequency = useMemo(() => {
    const frequencyKey = String(formValues?.premiumFrequency ?? "yearly").toLowerCase();
    const divisor = PREMIUM_FREQUENCY_DIVISOR[frequencyKey] || 1.000;
    return Math.ceil(Number(minAllowedPremium || 0) * divisor);
  }, [formValues?.premiumFrequency, minAllowedPremium]);

 const resolveAllowedDpValues = useCallback(
  (pptRaw) => {
    const ppt = String(pptRaw ?? "");

    const raw = (
      currentProductConfig?.pptToDp?.[ppt] ||
      currentProductConfig?.dpOptions ||
      []
    ).map((value) => String(value));

    // If neither annuitant age is available,
    // don't filter the options.
    if (!Number.isFinite(olderAnnuitantAge)) {
      return raw;
    }

    return raw.filter((dp) => {
      const dpNum = Number(dp);

      if (!Number.isFinite(dpNum)) return false;

      // Use the OLDER annuitant's age
      return olderAnnuitantAge + dpNum <= 80;
    });
  },
  [currentProductConfig, olderAnnuitantAge],
);

  const getPremiumAmountValidationError = useCallback(
    (rawValue) => {
      const str = String(rawValue ?? "").trim();
      if (!str) return "Premium Amount is required.";
      const num = Number(str);
      if (!Number.isFinite(num) || num <= 0) {
        return "Please enter a valid Premium Amount.";
      }
      if (num > MAX_PREMIUM_AMOUNT) {
        return `Maximum Premium Amount allowed is ₹ ${MAX_PREMIUM_AMOUNT.toLocaleString("en-IN")}.`;
      }
      if (num < minAllowedPremiumByFrequency) {
        return `Minimum Premium Amount for selected plan/PPT and premium frequency is ₹ ${Number(
          minAllowedPremiumByFrequency,
        ).toLocaleString("en-IN")}.`;
      }
      return "";
    },
    [minAllowedPremiumByFrequency],
  );

  // useEffect(() => {
  //   if (!hasEditedPremiumAmount) return;

  //   const err = getPremiumAmountValidationError(formValues.premiumAmount);

  //   setPremiumAmountError(err);

  //   setFieldErrors((prev) => ({
  //     ...prev,
  //     ...(err ? { premiumAmount: err } : {})
  //   }));
  // }, [
  //   formValues.premiumAmount,
  //   formValues.premiumFrequency,
  //   formValues.premiumPayingTerm,
  //   activePlanOptionId,
  //   minAllowedPremiumByFrequency,
  // ]);

  const pptOptions = useMemo(() => {
    const raw = (currentProductConfig?.pptOptions || []).map((value) => ({
      value: String(value),
      label:
        currentProductConfig?.pptLabel?.[String(value)] || `${value} years`,
    }));
    return raw.filter((opt) => resolveAllowedDpValues(opt.value).length > 0);
  }, [currentProductConfig, resolveAllowedDpValues]);

  const dpOptions = useMemo(() => {
    const ppt = String(formValues?.premiumPayingTerm ?? "");
    const allowed = resolveAllowedDpValues(ppt);
    return allowed.map((value) => ({
      value: String(value),
      label: `${value} years`,
    }));
  }, [resolveAllowedDpValues, formValues?.premiumPayingTerm]);

  const premiumFrequencyOptions = useMemo(() => {
    if (!isSinglePayProduct) {
      return (
        formFields.find((f) => f.name === "premiumFrequency")?.options || []
      ).map((opt) => ({
        value: opt.value,
        label: typeof opt.label === "string" ? opt.label : opt.label?.en_US,
      }));
    }
    return [{ value: "yearly", label: "Single Pay" }];
  }, [isSinglePayProduct, formFields]);

useEffect(() => {
  if (!pptOptions.length) return;

  const selectedPpt = String(formValues?.premiumPayingTerm ?? "");

  // Nothing selected yet
  if (!selectedPpt) return;

  const nextDpOptions = resolveAllowedDpValues(selectedPpt);

  const selectedDp = String(formValues?.withdrawalAfter ?? "");

  // Single Pay:
  // Never auto-select Withdrawal After.
  if (isSinglePayProduct) {
    return;
  }

  // For regular products, keep existing valid value
  if (nextDpOptions.includes(selectedDp)) return;

  // For regular products only, auto-select first valid option
  const nextDp = nextDpOptions[0] || "";

  setFormValues((prev) => ({
    ...prev,
    withdrawalAfter: nextDp,
  }));
}, [
  pptOptions,
  formValues?.premiumPayingTerm,
  formValues?.withdrawalAfter,
  isSinglePayProduct,
  resolveAllowedDpValues,
]);

  useEffect(() => {
    if (!isSinglePayProduct) return;
    setFormValues((prev) => {
      const next = {
        ...prev,
        premiumPayingTerm: "1",
        premiumFrequency: "yearly",
        premiumAmount: "",
        withdrawalAfter: "",
      };
      const noChange =
        String(prev?.premiumPayingTerm ?? "") === "1" &&
        String(prev?.premiumFrequency ?? "").toLowerCase() === "yearly" &&
        String(prev?.premiumAmount ?? "") === "" && 
        String(prev?.withdrawalAfter ?? "") === "";
      return noChange ? prev : next;
    });
    setPremiumAmountError("");
    setHasEditedPremiumAmount(false);
    setFieldErrors((prev) => {
      if (!prev?.premiumAmount) return prev;
      const next = { ...prev };
      delete next.premiumAmount;
      return next;
    });
  }, [isSinglePayProduct, minAllowedPremiumByFrequency, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;


    setFormValues((prev) => {
      const updated = { ...prev, [name]: value };
      const nextErrors = { ...fieldErrors };


      if (name === "premiumPayingTerm") {
  const allowed = resolveAllowedDpValues(String(value));

  if (isSinglePayProduct) {
    // Single Pay → keep Withdrawal After unselected
    updated.withdrawalAfter = "";
  } else if (!allowed.includes(String(updated.withdrawalAfter ?? ""))) {
    // Regular products → auto-select first valid option
    updated.withdrawalAfter = allowed[0] || "";
  }
}
      if (name === "premiumAmount") {
        setHasEditedPremiumAmount(true);
      } else {
        const fieldMeta = formFields.find((f) => f.name === name);
        const isRequired = Boolean(fieldMeta?.required);
        const isBlank = String(value ?? "").trim() === "";
        if (isRequired && isBlank) {
          nextErrors[name] = `${fieldMeta?.label?.en_US || name} is required.`;
        } else {
          delete nextErrors[name];
        }
      }
     
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) {
        return updated;
      }
      // dispatch(journeyActions.setCustomizePlanData(updated));
      setIsFormDirtyAfterCalculate(true);

      return updated;
    });
  };

  //   useEffect(() => {
  //   if (!payableAmount) return;

  //   setFormValues((prev) => {
  //     const updated = {
  //       ...prev,
  //       premiumAmount: String(payableAmount),
  //     };
  //     return updated;
  //   });
  // }, [payableAmount, dispatch]);

  //  Render field dynamically
  const renderField = (field) => {
    switch (field.type) {
      case "select":
        return (
          <select
            name={field.name}
            value={formValues[field.name]}
            onChange={handleChange}
            className="form-input"
            disabled={
              isSinglePayProduct &&
              (field.name === "premiumPayingTerm" ||
                field.name === "premiumFrequency")
            }
          >
            <option value="">Select</option>
            {(field.name === "premiumPayingTerm"
              ? pptOptions
              : field.name === "premiumFrequency"
                ? premiumFrequencyOptions
                : field.name === "withdrawalAfter"
                  ? dpOptions
                  : field.options || []
            ).map((opt, i) => (
              <option key={i} value={opt.value}>
                {typeof opt.label === "string" ? opt.label : opt.label?.en_US}
              </option>
            ))}
          </select>
        );

      case "numeric":
        return (
          <input
            type="text"
            name={field.name}
            value={
              formValues[field.name]
                ? Number(formValues[field.name]).toLocaleString("en-IN")
                : ""
            }
            placeholder={field.placeholder?.en_US}
            onChange={(e) => {
              let value = e.target.value.replaceAll(",", "");

              if (!/^\d*$/.test(value)) return;
              handleChange({
                target: {
                  name: field.name,
                  value,
                },
              });
            }}
            className="form-input "
          />
        );

      case "text":
      default:
        return (
          <input
            type="text"
            name={field.name}
            value={formValues[field.name]}
            placeholder={field.placeholder?.en_US}
            onChange={handleChange}
            className="form-input "
          />
        );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = {};

    // Validate all required fields
    formFields.forEach((field) => {
      const value = formValues?.[field.name];

      if (field.required && String(value ?? "").trim() === "") {
        errors[field.name] = `${field.label?.en_US || field.name} is required.`;
      }
    });

    // validate Premium Amount on Calculate
    const premiumError = getPremiumAmountValidationError(
      formValues?.premiumAmount
    );

    if (premiumError) {
      errors.premiumAmount = premiumError;
    }

    // Update errors so they are displayed
    setFieldErrors(errors);
    setPremiumAmountError(premiumError);

    if (Object.keys(errors).length > 0) {
      return;
    }

    dispatch(journeyActions.setCustomizePlanData(formValues));
    setIsFormDirtyAfterCalculate(false);
    setIsCalculateClicked(true);
  };

  return (
    <div className="customise-plan-conatiner premium-summary-section">
      <div className="customise-plan-header">
        <h5 className="semibold">{fieldset?.label?.en_US}</h5>
        <svg
          width="60"
          height="50"
          viewBox="0 0 60 50"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.63428 6.7092H45.5653L45.5933 6.67561C47.1964 4.7518 47.1964 1.9574 45.5933 0.0335921L45.5653 0H42.9824H8.63428L8.81688 0.196553C10.5305 2.04074 10.464 4.91418 8.66682 6.67712L8.63428 6.7092Z"
            fill="#E35055"
          />
          <path
            d="M38.2105 49.9999H4.49072V3.97899C4.49072 1.83132 6.23169 0.0903533 8.37925 0.0903533H41.3682C41.3682 0.0903533 44.7918 -0.112942 44.7228 0.0903533C42.3208 0.137312 42.4439 6.79944 42.4439 6.79944V45.7665C42.4439 48.1045 40.5486 49.9999 38.2105 49.9999Z"
            fill="#EE7B7F"
          />
          <path
            d="M38.1613 50.0002C36.6573 50.0002 1.23035 50.0002 1.23035 50.0002L1.20233 49.9666C-0.400778 48.0428 -0.400778 45.2484 1.20233 43.3246L1.23035 43.291H38.1613L37.9787 43.4876C36.2651 45.3319 35.974 48.4782 38.1613 50.0002Z"
            fill="#E35055"
          />
          <path
            d="M38.8302 40.3389H28.5723V40.9679H38.8302V40.3389Z"
            fill="#FDF3F3"
          />
          <path
            d="M38.8299 13.0908H9.50195V13.8751H38.8299V13.0908Z"
            fill="white"
          />
          <path
            d="M38.8299 16.9404H9.50195V17.7247H38.8299V16.9404Z"
            fill="white"
          />
          <path
            d="M38.8299 20.791H9.50195V21.5753H38.8299V20.791Z"
            fill="white"
          />
          <path
            d="M38.8299 24.6406H9.50195V25.4249H38.8299V24.6406Z"
            fill="white"
          />
          <path
            d="M38.8299 28.4912H9.50195V29.2754H38.8299V28.4912Z"
            fill="white"
          />
          <path
            d="M38.8299 36.1904H9.50195V36.9747H38.8299V36.1904Z"
            fill="white"
          />
          <path
            d="M38.8299 32.3408H9.50195V33.1251H38.8299V32.3408Z"
            fill="white"
          />
          <path
            d="M41.8746 35.882L40.1738 34.8626L41.334 32.1931L52.3769 13.7715L54.8337 15.2443L43.799 33.6523L41.8746 35.882Z"
            fill="#153E49"
          />
          <path
            d="M53.765 17.4652L50.8484 15.7168C50.7944 15.6843 50.7768 15.6144 50.8092 15.5603L56.099 6.73611C56.1314 6.68206 56.2014 6.66451 56.2554 6.69694L59.1721 8.44534C59.2262 8.47777 59.2437 8.54775 59.2113 8.6018L53.9215 17.426C53.889 17.4801 53.8189 17.4976 53.765 17.4652Z"
            fill="#113542"
          />
          <path
            d="M58.9496 9.6162L58.8586 9.56168L55.3874 7.48084C55.3439 7.45481 55.2876 7.46887 55.2615 7.51234L55.0399 7.88197C55.0139 7.92544 55.028 7.98182 55.0714 8.00785L58.4641 10.0416C58.5076 10.0677 58.5216 10.124 58.4956 10.1675L55.5665 15.0538C55.5404 15.0973 55.5545 15.1535 55.598 15.1797L55.9504 15.3909C55.9939 15.4169 56.0502 15.4028 56.0763 15.3594L59.3213 9.94607C59.3474 9.9026 59.3333 9.84623 59.2898 9.82019L58.9496 9.6162Z"
            fill="#1A1A1A"
          />
          <path
            d="M40.24 37.7725L39.1841 37.1397L41.3341 32.1924L43.7909 33.6652L40.24 37.7725Z"
            fill="#5D7A7F"
          />
          <path
            d="M40.2398 37.7724L39.184 37.1396L38.6294 37.497L38.1455 39.6594L38.4274 39.8284L40.1539 38.3802L40.2398 37.7724Z"
            fill="#DAE7E8"
          />
          <path
            d="M43.8363 33.9334L41.0209 32.2457C40.9463 32.2009 40.922 32.1041 40.9668 32.0295L40.9684 32.0268C41.0131 31.9522 41.11 31.9279 41.1846 31.9726L43.9999 33.6604C44.0745 33.7051 44.0988 33.8019 44.0541 33.8766L44.0525 33.8793C44.0077 33.9539 43.9109 33.9781 43.8363 33.9334Z"
            fill="#688689"
          />
          <path
            d="M43.6068 34.3162L40.7914 32.6285C40.7168 32.5837 40.6925 32.4869 40.7373 32.4123L40.7389 32.4096C40.7837 32.335 40.8805 32.3107 40.9551 32.3554L43.7704 34.0432C43.8451 34.0879 43.8693 34.1848 43.8246 34.2594L43.823 34.262C43.7782 34.3367 43.6814 34.361 43.6068 34.3162Z"
            fill="#688689"
          />
          <path
            d="M39.295 38.5491C39.2294 38.6586 39.0875 38.694 38.978 38.6285C38.8685 38.5628 38.8331 38.421 38.8986 38.3115C38.9642 38.202 39.1061 38.1666 39.2156 38.2321C39.3251 38.2978 39.3607 38.4397 39.295 38.5491Z"
            fill="#153E49"
          />
          <path
            d="M38.995 38.4977L38.2583 39.7266L38.3539 39.7839L39.0906 38.555L38.995 38.4977Z"
            fill="#153E49"
          />
          <path
            opacity="0.28"
            d="M51.1147 15.877L53.5716 17.3497L52.213 19.616L51.1147 15.877Z"
            fill="black"
          />
        </svg>
      </div>

      <div className="customise-plan-body">
        <div className="customise-plan-body-heading">
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          >
            <rect
              x="0.5"
              y="0.5"
              width="39"
              height="39"
              rx="19.5"
              fill="#F8F8F8"
            />
            <rect
              x="0.5"
              y="0.5"
              width="39"
              height="39"
              rx="19.5"
              stroke="#E7E7E7"
            />
            <rect
              x="8"
              y="8.43457"
              width="24"
              height="24"
              fill="url(#pattern0_33821_36504)"
            />
            <defs>
              <pattern
                id="pattern0_33821_36504"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use href="#image0_33821_36504" transform="scale(0.00416667)" />
              </pattern>
              <image
                id="image0_33821_36504"
                width="240"
                height="240"
                preserveAspectRatio="none"
                href="data:image/png;base64,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"
              />
            </defs>
          </svg>

          <span>{TEXT_CONTENT.CUSTOMIZE_PN}</span>
        </div>
        <form className="customise-plan-form" onSubmit={handleSubmit}>
          <div className="customise-plan-form-fields">
            {formFields.map((field, index) => (
              <div
                key={index}
                className="form-row "
                data-tour={
                  field.name === "premiumAmount" ||
                  field.name === "premiumPayingTerm" ||
                  field.name === "premiumFrequency" ||
                  field.name === "annuityFrequency"
                    ? field.name
                    : undefined
                }
              >
                <label className="label-one m-0">{field.label?.en_US}</label>
                <div
                  className={`input-wrapper ${field.name === "premiumAmount" ? "currency-input-wrapper" : ""}`}
                >
                  {field.name === "premiumAmount" && (
                    <span className="currency-symbol">₹</span>
                  )}
                  {renderField(field)}
                </div>
                {(fieldErrors[field.name] ||
                  (field.name === "premiumAmount" && hasEditedPremiumAmount
                    ? premiumAmountError
                    : "")) && (
                    <div className="error-text">
                      {fieldErrors[field.name] ||
                        (field.name === "premiumAmount" && hasEditedPremiumAmount
                          ? premiumAmountError
                          : "")}
                    </div>
                  )}
              </div>
            ))}
          </div>

          <div
            className="submit-btn-wrapper mt-3"
            style={hideCalculateButton ? { display: "none" } : undefined}
          >
            <button
              type="submit"
              className="submit-btn btn primary-button regular"
              data-tour="calculateButton"
              data-calculated={isCalculateClicked ? "true" : "false"}
            >
              Calculate
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CustomizePlan;
